import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";
import { PillTheme } from "../../../pill-theme";
import { ShopifyGraphQLProductSchema as ProductDataSchema } from "../../../product";

export enum ProductExchangeRecommendationTag {
  CART = "cart",
  BROWSED = "browsed",
  FOR_YOU = "foryou",
  RECOMMENDED = "recommended",
}
export const ProductExchangeRecommendationTagSchema = createEnum(
  ProductExchangeRecommendationTag,
  { enumName: "ProductExchangeRecommendationTag" },
);

export const ProductExchangeRecommendationSchema = z.object({
  product: ProductDataSchema,
  recommendationTitle: z.string(),
  recommendationSubtitle: z.string(),
  recommendationTag: z.enum(ProductExchangeRecommendationTag),
  productAsVariantProductIds: z.array(z.string()).optional(),
});

export type ProductExchangeRecommendation = z.infer<
  typeof ProductExchangeRecommendationSchema
>;

export const ProductExchangeOrSectionInfoSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("product"),
    product: ProductExchangeRecommendationSchema,
  }),
  z.object({
    type: z.literal("section"),
    sectionTitle: z.string(),
    sectionSubtitle: z.string(),
  }),
]);

export type ProductExchangeOrSectionInfo = z.infer<
  typeof ProductExchangeOrSectionInfoSchema
>;

export interface ProductExchangeRecommendationV2
  extends ProductExchangeRecommendation {
  sectionTitle?: string;
  sectionSubtitle?: string;
}

// Use z.ZodType<T> to constrain the schema type
export const ProductExchangeRecommendationSchemaV2: z.ZodType<ProductExchangeRecommendationV2> =
  ProductExchangeRecommendationSchema.extend({
    sectionTitle: z.string().optional(),
    sectionSubtitle: z.string().optional(),
  });

export interface ExchangeRecommendationBadge {
  text: string;
  pillTheme: PillTheme;
}
export type WithExchangeRecommendationBadge<T> = T & {
  badge?: ExchangeRecommendationBadge;
};
export type WithProductAsVariantProductIds<T> = T & {
  productAsVariantProductIds?: string[];
};

export function getFormattedExchangeRecommendationTag(
  tag: ProductExchangeRecommendationTag,
): ExchangeRecommendationBadge {
  switch (tag) {
    case ProductExchangeRecommendationTag.CART:
      return { text: "Cart history", pillTheme: PillTheme.SOLID_GREEN };
    case ProductExchangeRecommendationTag.BROWSED:
      return { text: "Recently viewed", pillTheme: PillTheme.SOLID_LIGHT_BLUE };
    case ProductExchangeRecommendationTag.FOR_YOU:
      return { text: "For you", pillTheme: PillTheme.SOLID_PINK };
    case ProductExchangeRecommendationTag.RECOMMENDED:
      return { text: "Recommended", pillTheme: PillTheme.SOLID_ORANGE };
  }
}
